/*
===============================================================================
bieloadsession.apis.ts

Direct client API functions for:
    service: bieloadsession

Pack 5E historical-baseline semantics.
No Axios.
No React Router.
No API functions passed through page props.
===============================================================================
*/

import { json, postBiz, rows } from '../core/coreapi';

import {
  BieLoadSession,
  BieLoadSessionAddPayload,
  BieLoadSessionCancelPayload,
  BieLoadSessionComboItem,
  BieLoadSessionComboRequest,
  BieLoadSessionEditPayload,
  BieLoadSessionFindRequest,
  BieLoadSessionFindResult,
  BieLoadSessionScopeComboItem,
  BieLoadSessionScopeComboRequest,
  BieLoadSessionTransitionPayload,
  BieSaveResult,
  defaultBieLoadSessionFindRequest,
  IBieLoadSessionReopenResult,
  IBieLoadSessionStageResult,
} from './bieloadsession.models';

import {
  normalizeBieLoadSession,
  normalizeBieLoadSessionComboItem,
  normalizeBieLoadSessionFindResult,
  normalizeBieLoadSessionReopenResult,
  normalizeBieLoadSessionScopeComboItem,
  normalizeBieLoadSessionStageResult,
  normalizeBieSaveResult,
} from './bieloadsession.normalizers';
import { normalizeLoadSessionId, normalizeLoadSessionStamp } from './bieonboarding.normalize';

const SERVICE = 'bieloadsession';

export async function findBieLoadSessions(
  request: BieLoadSessionFindRequest = {},
): Promise<BieLoadSessionFindResult> {
  const normalizedRequest = {
    ...defaultBieLoadSessionFindRequest,
    ...request,
  };

  const response = await postBiz({
    s: SERVICE,
    a: 'find',

    sch: normalizedRequest.search || '',
    stc: normalizedRequest.stateCode || '',
    scc: normalizedRequest.scopeCode || '',
    ssy: normalizedRequest.sourceSystem || '',
    csd: normalizedRequest.coverageStartDate || null,
    ced: normalizedRequest.coverageEndDate || null,
    sts: normalizedRequest.status,
    pos: normalizedRequest.pageOffset,
    plm: normalizedRequest.pageLimit,
  });

  return normalizeBieLoadSessionFindResult(
    rows(response),
    normalizedRequest.pageOffset,
    normalizedRequest.pageLimit,
  );
}

export async function getBieLoadSession(
  loadSessionId: number,
): Promise<BieLoadSession> {
  const response = await postBiz({
    s: SERVICE,
    a: 'get',
    rid: loadSessionId,
  });

  return normalizeBieLoadSession(json(response));
}

export async function addBieLoadSession(
  payload: BieLoadSessionAddPayload,
): Promise<BieSaveResult> {
  const response = await postBiz({
    s: SERVICE,
    a: 'add',

    sci: payload.scopeId,
    nam: payload.name.trim(),
    shc: payload.shortcode.trim().toUpperCase(),
    dsc: payload.description?.trim() || null,
    sco: payload.sourceOrganization.trim(),
    ssy: payload.sourceSystem.trim(),
    csd: payload.coverageStartDate || null,
    ced: payload.coverageEndDate || null,
    hcd: payload.historicalCutoffDate || null,
    exi: payload.extraInfo || {},
  });

  return normalizeBieSaveResult(json(response));
}

export async function editBieLoadSession(
  payload: BieLoadSessionEditPayload,
): Promise<BieSaveResult> {
  const response = await postBiz({
    s: SERVICE,
    a: 'edit',

    rid: payload.loadSessionId,
    sci: payload.scopeId,
    nam: payload.name.trim(),
    shc: payload.shortcode.trim().toUpperCase(),
    dsc: payload.description?.trim() || null,
    sco: payload.sourceOrganization.trim(),
    ssy: payload.sourceSystem.trim(),
    csd: payload.coverageStartDate || null,
    ced: payload.coverageEndDate || null,
    hcd: payload.historicalCutoffDate || null,
    exi: payload.extraInfo || {},
    stp: payload.stamp,
  });

  return normalizeBieSaveResult(json(response));
}

export async function fetchBieLoadSessionCombo(
  request: BieLoadSessionComboRequest = {},
): Promise<BieLoadSessionComboItem[]> {
  const response = await postBiz({
    s: SERVICE,
    a: 'combo',

    sch: request.search || '',
    stc: request.stateCode || '',
    scc: request.scopeCode || '',
    lim: request.limit ?? 50,
  });

  return rows(response).map(
    normalizeBieLoadSessionComboItem,
  );
}

export async function fetchBieLoadSessionScopeCombo(
  request: BieLoadSessionScopeComboRequest = {},
): Promise<BieLoadSessionScopeComboItem[]> {
  const response = await postBiz({
    s: SERVICE,
    a: 'scopecombo',

    sch: request.search || '',
    lim: request.limit ?? 50,
  });
  return rows(response).map(
    normalizeBieLoadSessionScopeComboItem,
  );
}

export async function cancelBieLoadSession(
  payload: BieLoadSessionCancelPayload,
): Promise<BieSaveResult> {
  const response = await postBiz({
    s: SERVICE,
    a: 'cancel',
    rid: payload.loadSessionId,
    rmk: payload.remarks.trim(),
    stp: payload.stamp,
  });

  return normalizeBieSaveResult(json(response));
}

export async function stageBieLoadSession(
  payload: BieLoadSessionTransitionPayload,
): Promise<IBieLoadSessionStageResult> {
  const response = await postBiz({
    s: SERVICE,
    a: 'stage',
    rid: normalizeLoadSessionId(payload.loadSessionId),
    stp: normalizeLoadSessionStamp(payload.stamp),
  });

  return normalizeBieLoadSessionStageResult(json(response));
}

export async function reopenBieLoadSession(
  payload: BieLoadSessionTransitionPayload,
): Promise<IBieLoadSessionReopenResult> {
  const response = await postBiz({
    s: SERVICE,
    a: 'reopen',
    rid: normalizeLoadSessionId(payload.loadSessionId),
    stp: normalizeLoadSessionStamp(payload.stamp),
  });

  return normalizeBieLoadSessionReopenResult(json(response));
}


